export type AgentReadinessState = 'idle' | 'listening' | 'thinking' | 'speaking' | string;

export interface AgentReadinessOptions {
  onReady?: (state: AgentReadinessState) => void;
}

/**
 * Emits a one-shot readiness signal the first time an agent enters an active state
 * (listening, thinking, speaking). Re-arms itself whenever a new agent identity is
 * observed, so re-connection scenarios fire onReady again.
 */
export class AgentReadinessDetector {
  private currentAgentIdentity: string | null = null;
  private readyEmitted = false;
  private readonly onReady?: (state: AgentReadinessState) => void;

  constructor(options: AgentReadinessOptions = {}) {
    this.onReady = options.onReady;
  }

  observe(state: AgentReadinessState, agentIdentity: string | null | undefined): void {
    if (agentIdentity && agentIdentity !== this.currentAgentIdentity) {
      this.currentAgentIdentity = agentIdentity;
      this.readyEmitted = false;
    }
    if (this.readyEmitted) return;
    if (state === 'listening' || state === 'thinking' || state === 'speaking') {
      this.readyEmitted = true;
      this.onReady?.(state);
    }
  }

  reset(): void {
    this.currentAgentIdentity = null;
    this.readyEmitted = false;
  }

  getState(): { readyEmitted: boolean; agentIdentity: string | null } {
    return { readyEmitted: this.readyEmitted, agentIdentity: this.currentAgentIdentity };
  }
}
